import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContext } from 'react-dnd';
import { func, string, number, object, array } from 'prop-types';
import get from 'lodash/get';

import { UploadBox, TextInput, InputSelect, toJS } from 'components';

import { getFileExt } from '../common/fileUtils';
import {
    fillOrderInformation,
    uploadAssetFile,
    downloadAssetFile,
    removeAssetFile,
    saveInfoAfterPayment
} from './actions';
import { fetchOrderList } from '../customer/actions';
import { CURRENCIES, DEFAULT_CURRENCY } from 'common/const';
//import { MAX_UPLOAD_FILE_SIZE } from './constants';
import { MIN_PAYMENT_AMOUNT } from './constants';
import FileDownload from './FileDownload';

import './OrderInformation-style.css';

const ASSET_FILE_PREFIXS = {
    originalLanguage: '[original]',
    isLegacyTranslation: '[legacy]',
    isGlossary: '[glossary]',
    isStyleGuide: '[styleguide]',
    isScreenshots: '[screenshots]'
};

const TARGET_AGE_OPTIONS = [
    { value: 3, label: '3+' },
    { value: 7, label: '7+' },
    { value: 12, label: '12+' },
    { value: 16, label: '16+' },
    { value: 18, label: '18+' }
];
const GAME_CATEGORY_OPTIONS = [
    { value: 'Action', label: 'Action' },
    { value: 'Adult/Hentai', label: 'Adult/Hentai' },
    { value: 'Adventure', label: 'Adventure' },
    { value: 'Arcade', label: 'Arcade' },
    { value: 'Battle Royale', label: 'Battle Royale' },
    { value: 'Board', label: 'Board' },
    { value: 'Card', label: 'Card' },
    { value: 'Casino', label: 'Casino' },
    { value: 'Casual', label: 'Casual' },
    { value: 'City Building', label: 'City Building' },
    { value: 'Dice', label: 'Dice' },
    { value: 'Educational', label: 'Educational' },
    { value: 'Electronic Sports', label: 'Electronic Sports' },
    { value: 'Family', label: 'Family' },
    { value: 'Fantasy', label: 'Fantasy' },
    { value: 'Fighting', label: 'Fighting' },
    { value: 'FPS', label: 'FPS' },
    { value: 'Horror', label: 'Horror' },
    { value: 'Kids', label: 'Kids' },
    { value: 'Match 3', label: 'Match 3' },
    { value: 'MMO RPG', label: 'MMO RPG' },
    { value: 'MOBA', label: 'MOBA' },
    { value: 'Music', label: 'Music' },
    { value: 'Platformer', label: 'Platformer' },
    { value: 'Point & Click', label: 'Point & Click' },
    { value: 'Puzzle', label: 'Puzzle' },
    { value: 'Racing', label: 'Racing' },
    { value: 'Rogue - like', label: 'Rogue - like' },
    { value: 'RPG', label: 'RPG' },
    { value: 'RTS', label: 'RTS' },
    { value: 'Sandbox', label: 'Sandbox' },
    { value: 'Sci - Fi', label: 'Sci - Fi' },
    { value: 'Shooter', label: 'Shooter' },
    { value: 'Simulation', label: 'Simulation' },
    { value: 'Sports', label: 'Sports' },
    { value: 'Story - driven', label: 'Story - driven' },
    { value: 'Strategy', label: 'Strategy' },
    { value: 'Survival', label: 'Survival' },
    { value: 'Trivia', label: 'Trivia' },
    { value: 'Turn - based strategy', label: 'Turn - based strategy' },
    { value: 'Visual Novel', label: 'Visual Novel' },
    { value: 'Word', label: 'Word' }
];

const PLATFORMS = [
    { value: 'iOS', label: 'iOS' },
    { value: 'Android', label: 'Android' },
    { value: 'PS4', label: 'PS4' },
    { value: 'PS5', label: 'PS5' },
    { value: 'Xbox One', label: 'Xbox One' },
    { value: 'Xbox Series X', label: 'Xbox Series X' },
    { value: 'PC', label: 'PC' },
    { value: 'Stadia', label: 'Stadia' },
    { value: 'Switch', label: 'Switch' },
    { value: 'Nintendo DS', label: 'Nintendo DS' }
];

const SWEARING_OPTIONS = [
    { value: 'No', label: 'No' },
    { value: 'Mild (+12)', label: 'Mild (+12)' },
    { value: 'Strong (+16)', label: 'Strong (+16)' },
    { value: 'Realistic (+18)', label: 'Realistic (+18)' }
];

// ---- Game brief layout helpers (UI only, values match the options above) ----
const BRIEF_PLATFORM_ORDER = [
    'PC',
    'iOS',
    'Android',
    'Switch',
    'PS5',
    'Xbox Series X',
    'PS4',
    'Xbox One',
    'Stadia',
    'Nintendo DS'
];
const BRIEF_COMMON_GENRES = [
    'Action',
    'Adventure',
    'Casual',
    'Puzzle',
    'RPG',
    'Strategy',
    'Simulation',
    'Shooter',
    'Platformer',
    'Horror',
    'Story - driven',
    'Visual Novel'
];
const BRIEF_SWEAR_LABELS = {
    No: 'None',
    'Mild (+12)': 'Mild',
    'Strong (+16)': 'Strong',
    'Realistic (+18)': 'Realistic'
};
const BRIEF_SWEAR_FOR_AGE = {
    3: 'none',
    7: 'none',
    12: 'mild',
    16: 'strong',
    18: 'realistic'
};
const BRIEF_ADDRESS_LABELS = {
    informal: 'Informal',
    formal: 'Formal',
    follow: 'Let the linguist decide'
};
const BRIEF_ADDRESS_RESULT = {
    informal: 'German du, French tu, Spanish tú.',
    formal: 'German Sie, French vous, Spanish usted.',
    follow: 'Each linguist picks what sounds natural.'
};
// Optional questions on the "order placed" screen. Keys must match the
// server allowlist (order-service core/briefAfterPayment.js).
const AFTER_PAYMENT_PARTS = {
    1: {
        title: 'What kind of game is it?',
        why: 'Genre decides the terms linguists use, down to button names.',
        keys: ['gameCategories', 'webPage']
    },
    2: {
        title: 'Who plays it?',
        why:
            'This sets how strong the language can be and which gender forms to use.',
        keys: ['targetAge', 'swearing', 'targetGender']
    },
    3: {
        title: 'How does the game speak?',
        why:
            'Many languages have a formal and an informal “you”. This picks one.',
        keys: [
            'messagesAddress',
            'characterAddress',
            'isDialoguesInOrder',
            'dialoguesInOrderNote'
        ]
    },
    4: {
        title: 'Text details',
        why: 'Last part. These keep the text looking right in your game.',
        keys: ['isSpecialCharacter', 'isAdaptation', 'adaptationNote']
    }
};
const AFTER_PAYMENT_PART_COUNT = 4;
// Same limit as the server (order-service core/briefAfterPayment.js).
const BRIEF_NOTE_MAX = 2000;
// The main answers counted in "x answers left" (free-text notes and the
// store link are extras and are not counted). 'dialogue' is the
// "Do characters talk?" choice.
const COUNTED_ANSWERS = [
    'gameCategories',
    'targetAge',
    'swearing',
    'targetGender',
    'messagesAddress',
    'dialogue',
    'isSpecialCharacter',
    'isAdaptation'
];
const BRIEF_REF_TYPES = [
    { value: ASSET_FILE_PREFIXS.isGlossary, label: 'Glossary' },
    { value: ASSET_FILE_PREFIXS.isStyleGuide, label: 'Style guide' },
    {
        value: ASSET_FILE_PREFIXS.isLegacyTranslation,
        label: 'Earlier translations'
    },
    { value: ASSET_FILE_PREFIXS.isScreenshots, label: 'Screenshots or video' },
    {
        value: ASSET_FILE_PREFIXS.originalLanguage,
        label: 'Reference in the original language'
    }
];
const briefGenreLabel = genre =>
    genre.replace(' - ', '-').replace('Rogue-like', 'Roguelike');
const briefEmpty = value =>
    value === undefined || value === null || value === '';
const briefSame = (a, b) => !briefEmpty(a) && String(a) === String(b);

class OrderInformation extends React.PureComponent {
    static propTypes = {
        // 'before' (step 4, default) or 'after' (order placed screen)
        phase: string,
        onFinish: func,
        orderId: string,
        sourceWordCount: number,
        info: object,
        assetFiles: array,
        fillOrderInformation: func,
        saveInfoAfterPayment: func,
        // local preview only (npm start, ?preview=order-placed)
        previewSave: func
    };

    state = this.props.info || {};

    onInput = (id, text) => {
        this.setState({
            [id]: text
        });
    };

    onCheck = (id, value) => {
        this.setState({
            [id]: value
        });
    };

    onNoteInput = text => {
        this.setState({
            note: text
        });
    };

    buildDuplicatedFileName = (fileName, existFileNames, count) => {
        if (!existFileNames.includes(fileName)) {
            return fileName;
        }

        const fileExt = getFileExt(fileName);
        //replace(/ \(.*\)+\..*/,'')
        const newName =
            fileName.replace(/ \(.\)/, '').replace(`${fileExt}`, '') +
            ` (${count + 1})${fileExt}`;

        return this.buildDuplicatedFileName(newName, existFileNames, count + 1);
    };

    onFileInput = (assetId, files) => {
        const { orderId, assetFiles, uploadAssetFile } = this.props;

        uploadAssetFile(orderId, {
            name: this.buildDuplicatedFileName(
                `${assetId}${files[0].name}`,
                assetFiles || [],
                0
            ),
            file: files[0]
        });
    };

    onContinueClick = () => {
        const { orderId, fillOrderInformation } = this.props;
        const info = {
            version: 2
        };

        Object.keys(this.state).forEach(key => {
            if (this.state[key] !== undefined) {
                info[key] = this.state[key];
            }
        });

        fillOrderInformation(orderId, info);
    };

    buildAssetFileList = filterAssetId => {
        const {
            orderList = [],
            orderId,
            assetFiles,
            downloadAssetFile,
            removeAssetFile
        } = this.props;

        const { originalOrderUid } = this.state;
        const list = [];

        const referOrder = orderList
            ? orderList.find(order => order.uid === originalOrderUid) || {}
            : {};

        referOrder.assets &&
            referOrder.assets.forEach(fileName => {
                if (fileName.includes(filterAssetId)) {
                    const downloadFile = () => {
                        downloadAssetFile({
                            orderId,
                            referOrderId: originalOrderUid,
                            fileName,
                            newFileName: fileName.replace(filterAssetId, '')
                        });
                    };

                    list.push(
                        <FileDownload
                            key={`refer-asset-file-${fileName}`}
                            fileName={fileName.replace(filterAssetId, '')}
                            onDownload={downloadFile}
                        />
                    );
                }
            });

        assetFiles &&
            assetFiles.forEach(fileName => {
                if (fileName.includes(filterAssetId)) {
                    const downloadFile = () => {
                        downloadAssetFile({
                            orderId,
                            fileName,
                            newFileName: fileName.replace(filterAssetId, '')
                        });
                    };
                    const removeFile = () => {
                        removeAssetFile(orderId, fileName);
                    };

                    list.push(
                        <FileDownload
                            key={`asset-file-${fileName}`}
                            fileName={fileName.replace(filterAssetId, '')}
                            onDownload={downloadFile}
                            onRemove={removeFile}
                        />
                    );
                }
            });

        return <div className="asset-files">{list}</div>;
    };

    onExistingSelect = option => {
        const { orderList } = this.props;
        const referOrder =
            orderList.find(order => order.uid === option.value) || {};
        const referInfo = referOrder.info || {};

        this.setState({
            isTranslateGameTitle: undefined,
            gameCategories: undefined,
            targetAge: undefined,
            platforms: undefined,
            isGameReleased: undefined,
            gameReleaseDate: undefined,
            originalLanguage: undefined,
            isRemainTerms: undefined,
            remainTerms: undefined,
            isSpecialCharacter: undefined,
            specialCharactersNote: undefined,
            swearing: undefined,
            isStringLengthLimit: undefined,
            stringLengthPriority: undefined,
            stringLengthLimitNote: undefined,
            isAdaptation: undefined,
            adaptationNote: undefined,
            isDialoguesInOrder: undefined,
            dialoguesInOrderNote: undefined,
            characterAddress: undefined,
            messagesAddress: undefined,
            targetGender: undefined,
            note: undefined,
            webPage: undefined,
            ...referInfo,
            originalOrderUid: referInfo.originalOrderUid || referOrder.uid,
            originalOrderId: referInfo.originalOrderId || referOrder.id
        });
    };

    onGameTitleInput = text => {
        this.setState({
            originalOrderUid: '',
            originalOrderId: '',
            gameTitle: text
        });
    };

    componentDidMount() {
        const { user, fetchOrderList } = this.props;

        if (user) {
            fetchOrderList({ limit: 100 });
        }
    }

    componentDidUpdate(prevProps) {
        const { user, fetchOrderList } = this.props;

        if (!prevProps.user && !!user) {
            fetchOrderList({ limit: 100 });
        }
    }

    // ------------------------------------------------------------------
    // Game brief.
    //
    // phase "before" (step 4): only the questions the project managers
    // need to start. Sent once with fillOrderInformation, same keys and
    // values as always.
    // phase "after" (order placed screen): the other questions, optional,
    // in four short parts. Each part is saved on its own with
    // saveInfoAfterPayment; the server only accepts these keys.
    //
    // Screen-only state (open part, errors, saving...) lives in `this.ui`,
    // NOT in `this.state`, so it is never sent to the backend.
    // ------------------------------------------------------------------

    ui = {
        part: 1,
        showErrors: false,
        allGenres: false,
        hasDialogue: undefined,
        refType: ASSET_FILE_PREFIXS.isGlossary,
        // undefined: open only when files or a note are already there.
        showExtras: undefined,
        saving: false,
        saveError: false
    };

    // Values the server already has (after payment), to send only changes.
    savedInfo = Object.assign({}, this.props.info || {});

    briefRoot = null;

    isAfterPayment = () => this.props.phase === 'after';

    setUi = (patch, focus) => {
        this.ui = { ...this.ui, ...patch };
        this.forceUpdate(() => {
            if (!focus || !this.briefRoot) {
                return;
            }
            if (focus === 'top' && this.briefRoot.scrollIntoView) {
                this.briefRoot.scrollIntoView({ block: 'start' });
            }
            const target = this.briefRoot.querySelector(
                focus === 'top' ? '.brief-title' : '.brief-notice'
            );
            if (target) {
                if (focus !== 'top' && target.scrollIntoView) {
                    target.scrollIntoView({ block: 'center' });
                }
                if (target.focus) {
                    target.focus({ preventScroll: true });
                }
            }
        });
    };

    getHasDialogue = () => {
        if (this.ui.hasDialogue !== undefined) {
            return this.ui.hasDialogue;
        }
        const { isDialoguesInOrder, characterAddress } = this.state;
        if (briefEmpty(isDialoguesInOrder) && !characterAddress) {
            return null;
        }
        if (isDialoguesInOrder === 'na' && characterAddress === 'follow') {
            return false;
        }
        return true;
    };

    // How many of the counted answers are still empty.
    answersLeft = () =>
        COUNTED_ANSWERS.filter(key => {
            if (key === 'dialogue') {
                const hasDialogue = this.getHasDialogue();
                return hasDialogue === null || hasDialogue === undefined;
            }
            const value = this.state[key];
            return Array.isArray(value) ? !value.length : briefEmpty(value);
        }).length;

    hasAssets = prefix => {
        const { orderList, assetFiles } = this.props;
        const { originalOrderUid } = this.state;
        const referOrder =
            (orderList || []).find(order => order.uid === originalOrderUid) ||
            {};
        const has = list =>
            (list || []).some(fileName => fileName.includes(prefix));
        return has(referOrder.assets) || has(assetFiles);
    };

    // ---------- before payment ----------

    // Only two answers are needed to continue: the title and a platform.
    // The other questions have a safe default that is shown on screen.
    missingBeforePayment = () => {
        const s = this.state;
        const missing = [];
        if (!s.gameTitle) {
            missing.push(['gameTitle', 'Your game’s title']);
        }
        if (!s.platforms || !s.platforms.length) {
            missing.push(['platforms', 'At least one platform']);
        }
        return missing;
    };

    // What the screen shows when the customer did not pick anything:
    // "Let our linguists decide", and both text boxes unticked (= No).
    beforePaymentDefaults = () => {
        const s = this.state;
        const defaults = {};
        if (briefEmpty(s.isTranslateGameTitle)) {
            defaults.isTranslateGameTitle = 'follow';
        }
        if (briefEmpty(s.isStringLengthLimit)) {
            defaults.isStringLengthLimit = false;
        }
        if (briefEmpty(s.isRemainTerms) && !s.remainTerms) {
            defaults.isRemainTerms = false;
        }
        return defaults;
    };

    onBriefFinish = () => {
        if (this.missingBeforePayment().length) {
            this.setUi({ showErrors: true }, 'notice');
            return;
        }
        const defaults = this.beforePaymentDefaults();
        if (Object.keys(defaults).length) {
            this.setState(defaults, this.onContinueClick);
            return;
        }
        this.onContinueClick();
    };

    onReuseGame = option => {
        this.onExistingSelect(option);
        this.setUi({ hasDialogue: undefined, showErrors: false });
    };

    onHasDialogue = value => {
        const { isDialoguesInOrder, characterAddress } = this.state;
        if (value === false) {
            this.setState({
                isDialoguesInOrder: 'na',
                characterAddress: 'follow'
            });
        } else if (
            isDialoguesInOrder === 'na' &&
            characterAddress === 'follow'
        ) {
            this.setState({
                isDialoguesInOrder: undefined,
                characterAddress: undefined
            });
        }
        this.setUi({ hasDialogue: value });
    };

    // ---------- after payment ----------

    afterPaymentPayload = part => {
        const payload = {};
        AFTER_PAYMENT_PARTS[part].keys.forEach(key => {
            const value = this.state[key];
            if (
                value !== undefined &&
                JSON.stringify(value) !== JSON.stringify(this.savedInfo[key])
            ) {
                payload[key] = value;
            }
        });
        // "No dialogue" is stored as isDialoguesInOrder 'na'. When the
        // customer now says the characters do talk, clear that value.
        if (
            part === 3 &&
            this.getHasDialogue() === true &&
            this.state.isDialoguesInOrder === undefined &&
            this.savedInfo.isDialoguesInOrder === 'na'
        ) {
            payload.isDialoguesInOrder = null;
        }
        return payload;
    };

    nextAfterPaymentPart = part => {
        if (part >= AFTER_PAYMENT_PART_COUNT) {
            this.finishAfterPayment('done');
            return;
        }
        this.setUi({ part: part + 1, saving: false, saveError: false }, 'top');
    };

    finishAfterPayment = how => {
        const { onFinish } = this.props;
        if (onFinish) {
            onFinish(how, {
                answered: COUNTED_ANSWERS.length - this.answersLeft(),
                total: COUNTED_ANSWERS.length
            });
        }
    };

    onSaveAfterPaymentPart = () => {
        const { orderId, previewSave } = this.props;
        // previewSave: local preview only, nothing is sent anywhere.
        const saveInfoAfterPayment =
            previewSave || this.props.saveInfoAfterPayment;
        const { part, saving } = this.ui;
        if (saving) {
            return;
        }
        const payload = this.afterPaymentPayload(part);
        if (!Object.keys(payload).length) {
            this.nextAfterPaymentPart(part);
            return;
        }
        this.setUi({ saving: true, saveError: false });
        Promise.resolve(saveInfoAfterPayment(orderId, payload))
            .then(() => {
                Object.assign(this.savedInfo, payload);
                this.nextAfterPaymentPart(part);
            })
            .catch(() => {
                // Keep every answer on screen; the customer can try again
                // or skip. The order itself is already placed.
                this.setUi({ saving: false, saveError: true }, 'notice');
            });
    };

    // ---------- shared pieces ----------

    renderError = (key, text = 'Please choose an option') =>
        this.ui.showErrors && this.briefErrors[key] ? (
            <p className="brief-err">{text}</p>
        ) : null;

    renderRadios = (name, options, current, onPick) => (
        <div className="brief-list">
            {options.map(option => (
                <label className="brief-row" key={String(option.value)}>
                    <input
                        type="radio"
                        name={name}
                        checked={briefSame(current, option.value)}
                        onChange={() => onPick(option.value)}
                    />
                    <span>
                        {option.label}
                        {option.hint && (
                            <span className="brief-row-hint">
                                {option.hint}
                            </span>
                        )}
                    </span>
                </label>
            ))}
        </div>
    );

    renderChecks = (options, current, onChange) => {
        const selected = current || [];
        return (
            <div className="brief-grid">
                {options.map(option => {
                    const isOn = selected.indexOf(option.value) !== -1;
                    return (
                        <label className="brief-row" key={option.value}>
                            <input
                                type="checkbox"
                                checked={isOn}
                                onChange={() =>
                                    onChange(
                                        isOn
                                            ? selected.filter(
                                                  value =>
                                                      value !== option.value
                                              )
                                            : selected.concat([option.value])
                                    )
                                }
                            />
                            <span>{option.label}</span>
                        </label>
                    );
                })}
            </div>
        );
    };

    renderNotice = (title, items = []) => (
        <div className="brief-notice" role="alert" tabIndex={-1}>
            <svg
                aria-hidden="true"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B42318"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16.5h.01" />
            </svg>
            <div>
                <p className="brief-notice-title">{title}</p>
                {items.length > 0 && (
                    <ul>
                        {items.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );

    // ---------- questions asked before payment ----------

    // Pill buttons for short choices. Real radios / checkboxes inside, so
    // keyboard and screen readers work as usual.
    renderChips = (name, options, isOn, onToggle, type = 'radio') => (
        <div className="brief-chips">
            {options.map(option => (
                <label className="brief-chip" key={String(option.value)}>
                    <input
                        type={type}
                        name={name}
                        checked={isOn(option.value)}
                        onChange={() => onToggle(option.value)}
                    />
                    <span>{option.label}</span>
                </label>
            ))}
        </div>
    );

    renderGameQuestions = gameOptions => {
        const { gameTitle, isTranslateGameTitle, platforms } = this.state;
        const titleChoice = briefEmpty(isTranslateGameTitle)
            ? 'follow'
            : isTranslateGameTitle;
        const selectedPlatforms = platforms || [];
        const platformOptions = BRIEF_PLATFORM_ORDER.map(value =>
            PLATFORMS.find(option => option.value === value)
        ).filter(Boolean);
        return (
            <React.Fragment>
                <label className="brief-q">
                    <span className="brief-label">
                        What’s your game called?
                    </span>
                    {gameOptions.length > 0 && (
                        <span className="brief-help">
                            Ordered for this game before? Pick it from the list
                            and we’ll reuse your earlier answers.
                        </span>
                    )}
                    <InputSelect
                        value={gameTitle}
                        options={gameOptions}
                        onSelectChange={this.onReuseGame}
                        onInputChange={this.onGameTitleInput}
                        placeholder={
                            gameOptions.length
                                ? 'Type the title or pick an earlier game'
                                : 'Your game’s title'
                        }
                    />
                    {this.renderError(
                        'gameTitle',
                        'Please enter your game’s title.'
                    )}
                </label>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        How would you like us to handle the title?
                    </legend>
                    {this.renderChips(
                        'brief-translate-title',
                        [
                            { label: 'Keep it as it is', value: false },
                            { label: 'Translate it', value: true },
                            {
                                label: 'Let our linguists decide',
                                value: 'follow'
                            }
                        ],
                        value => briefSame(titleChoice, value),
                        value => this.onCheck('isTranslateGameTitle', value)
                    )}
                </fieldset>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Which platforms is it on?{' '}
                        <span className="brief-opt">Select all that apply</span>
                    </legend>
                    {this.renderChips(
                        'brief-platforms',
                        platformOptions,
                        value => selectedPlatforms.indexOf(value) !== -1,
                        value =>
                            this.onCheck(
                                'platforms',
                                selectedPlatforms.indexOf(value) !== -1
                                    ? selectedPlatforms.filter(v => v !== value)
                                    : selectedPlatforms.concat([value])
                            ),
                        'checkbox'
                    )}
                    {this.renderError(
                        'platforms',
                        'Please pick at least one platform.'
                    )}
                </fieldset>
            </React.Fragment>
        );
    };

    // Two yes/no questions as one optional checklist: tick = yes,
    // left empty = no. Same isStringLengthLimit / isRemainTerms values.
    renderTextRules = () => {
        const {
            isStringLengthLimit,
            stringLengthPriority,
            stringLengthLimitNote,
            isRemainTerms,
            remainTerms
        } = this.state;
        const lengthOn = isStringLengthLimit === true;
        const remainOn = isRemainTerms === true || !!remainTerms;
        return (
            <fieldset className="brief-q">
                <legend className="brief-label">
                    Anything special about your text?{' '}
                    <span className="brief-opt">Optional</span>
                </legend>
                <div className="brief-list brief-checklist">
                    <label className="brief-row">
                        <input
                            type="checkbox"
                            name="brief-length-limit"
                            checked={lengthOn}
                            onChange={() =>
                                this.onCheck('isStringLengthLimit', !lengthOn)
                            }
                        />
                        <span>
                            Length limits{' '}
                            <span className="brief-inline-hint">
                                like buttons or menus
                            </span>
                        </span>
                    </label>
                    {lengthOn && (
                        <div className="brief-indent">
                            <fieldset className="brief-q">
                                <legend className="brief-label">
                                    If a translation doesn’t fit, what matters
                                    more?{' '}
                                    <span className="brief-opt">Optional</span>
                                </legend>
                                {this.renderRadios(
                                    'brief-length-priority',
                                    [
                                        {
                                            label: 'Making it fit',
                                            value: 'length'
                                        },
                                        {
                                            label: 'Keeping the best wording',
                                            value: 'quality'
                                        }
                                    ],
                                    stringLengthPriority,
                                    value =>
                                        this.onCheck(
                                            'stringLengthPriority',
                                            value
                                        )
                                )}
                            </fieldset>
                            <label className="brief-q">
                                <span className="brief-label">
                                    Any length rules we should know?{' '}
                                    <span className="brief-opt">Optional</span>
                                </span>
                                <TextInput
                                    rows={2}
                                    value={stringLengthLimitNote}
                                    placeholder="For example: buttons max 12 characters"
                                    onChange={text =>
                                        this.onInput(
                                            'stringLengthLimitNote',
                                            text
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}
                    <label className="brief-row">
                        <input
                            type="checkbox"
                            name="brief-remain-terms"
                            checked={remainOn}
                            onChange={() => {
                                this.onCheck('isRemainTerms', !remainOn);
                                if (remainOn) {
                                    this.onCheck('remainTerms', '');
                                }
                            }}
                        />
                        <span>
                            Words to keep as they are{' '}
                            <span className="brief-inline-hint">
                                like names or brands
                            </span>
                        </span>
                    </label>
                    {remainOn && (
                        <div className="brief-indent">
                            <label className="brief-q">
                                <span className="brief-label">
                                    Which words? One per line{' '}
                                    <span className="brief-opt">Optional</span>
                                </span>
                                <TextInput
                                    rows={3}
                                    value={remainTerms}
                                    onChange={text =>
                                        this.onInput('remainTerms', text)
                                    }
                                />
                                <span className="brief-help">
                                    Not sure yet? Leave it empty, our linguists
                                    will ask you.
                                </span>
                            </label>
                        </div>
                    )}
                </div>
            </fieldset>
        );
    };

    renderExtras = () => {
        const { note } = this.state;
        const fileGroups = BRIEF_REF_TYPES.filter(type =>
            this.hasAssets(type.value)
        );
        return (
            <React.Fragment>
                <div className="brief-q">
                    <p className="brief-label">Files</p>
                    <p className="brief-help">One file at a time.</p>
                    <div className="brief-ref-box">
                        <div className="brief-ref-type">
                            <label
                                className="brief-sublabel"
                                htmlFor="brief-ref-type"
                            >
                                What kind of file is it?
                            </label>
                            <select
                                id="brief-ref-type"
                                className="brief-field brief-narrow"
                                value={this.ui.refType}
                                onChange={event =>
                                    this.setUi({ refType: event.target.value })
                                }
                            >
                                {BRIEF_REF_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <UploadBox
                            id="upload-box-brief-reference"
                            name="upload-trans-source"
                            onNewFiles={files =>
                                this.onFileInput(this.ui.refType, files)
                            }
                            disabled={false}
                        >
                            <span className="brief-drop">
                                Drag a file here, or{' '}
                                <span className="brief-drop-link">
                                    choose a file
                                </span>
                            </span>
                        </UploadBox>
                    </div>
                    {fileGroups.map(type => (
                        <div className="brief-files" key={type.value}>
                            <p className="brief-files-title">{type.label}</p>
                            {this.buildAssetFileList(type.value)}
                        </div>
                    ))}
                </div>
                <label className="brief-q">
                    <span className="brief-label">
                        A note for our linguists
                    </span>
                    <TextInput
                        rows={4}
                        value={note}
                        onChange={this.onNoteInput}
                    />
                    <span className="brief-help">
                        Links, access keys, builds or anything else.
                    </span>
                </label>
            </React.Fragment>
        );
    };

    // ---------- questions asked after payment ----------

    renderGenreAndLinks = () => {
        const { gameCategories, webPage } = this.state;
        const selectedGenres = gameCategories || [];
        const genreOptions = (this.ui.allGenres
            ? GAME_CATEGORY_OPTIONS.map(option => option.value)
            : BRIEF_COMMON_GENRES.concat(
                  selectedGenres.filter(
                      genre => BRIEF_COMMON_GENRES.indexOf(genre) === -1
                  )
              )
        ).map(value => ({ value, label: briefGenreLabel(value) }));

        return (
            <React.Fragment>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Genres{' '}
                        <span className="brief-opt">Select all that apply</span>
                    </legend>
                    {this.renderChecks(genreOptions, gameCategories, values =>
                        this.onCheck('gameCategories', values)
                    )}
                    <div>
                        <button
                            type="button"
                            className="brief-link"
                            aria-expanded={this.ui.allGenres}
                            onClick={() =>
                                this.setUi({ allGenres: !this.ui.allGenres })
                            }
                        >
                            {this.ui.allGenres
                                ? 'Show fewer genres'
                                : `Show all ${GAME_CATEGORY_OPTIONS.length} genres`}
                        </button>
                    </div>
                </fieldset>
                <label className="brief-q">
                    <span className="brief-label">
                        Store page or website{' '}
                        <span className="brief-opt">Optional</span>
                    </span>
                    <TextInput
                        value={webPage}
                        maxLength={BRIEF_NOTE_MAX}
                        placeholder="http://your-game-page.com"
                        rows={2}
                        onChange={text => this.onInput('webPage', text)}
                    />
                    <span className="brief-help">
                        One link per line. Linguists use it to see the art style
                        and store text.
                    </span>
                </label>
            </React.Fragment>
        );
    };

    renderAudience = () => {
        const { targetAge, swearing, targetGender } = this.state;
        const ageOption = TARGET_AGE_OPTIONS.find(option =>
            briefSame(targetAge, option.value)
        );
        return (
            <React.Fragment>
                <fieldset className="brief-q">
                    <legend className="brief-label">Age rating</legend>
                    {this.renderRadios(
                        'brief-age',
                        TARGET_AGE_OPTIONS,
                        targetAge,
                        value => this.onCheck('targetAge', value)
                    )}
                </fieldset>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Swearing and strong language
                    </legend>
                    {ageOption && (
                        <p className="brief-help">
                            Games rated {ageOption.label} usually choose{' '}
                            {BRIEF_SWEAR_FOR_AGE[ageOption.value]}.
                        </p>
                    )}
                    {this.renderRadios(
                        'brief-swearing',
                        SWEARING_OPTIONS.map(option => ({
                            value: option.value,
                            label: BRIEF_SWEAR_LABELS[option.value]
                        })),
                        swearing,
                        value => this.onCheck('swearing', value)
                    )}
                </fieldset>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        The player character can be
                    </legend>
                    {this.renderRadios(
                        'brief-gender',
                        [
                            { label: 'Female', value: 'female' },
                            { label: 'Male', value: 'male' },
                            {
                                label: 'Either, the player chooses',
                                value: 'both'
                            }
                        ],
                        targetGender,
                        value => this.onCheck('targetGender', value)
                    )}
                </fieldset>
            </React.Fragment>
        );
    };

    renderTone = () => {
        const {
            messagesAddress,
            characterAddress,
            dialoguesInOrderNote
        } = this.state;
        const hasDialogue = this.getHasDialogue();
        const addressOptions = ['informal', 'formal', 'follow'].map(value => ({
            value,
            label: BRIEF_ADDRESS_LABELS[value],
            hint: BRIEF_ADDRESS_RESULT[value]
        }));
        return (
            <React.Fragment>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        How should the game address the player?
                    </legend>
                    {this.renderRadios(
                        'brief-messages-address',
                        addressOptions,
                        messagesAddress,
                        value => this.onCheck('messagesAddress', value)
                    )}
                </fieldset>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Do characters talk to each other?
                    </legend>
                    {this.renderRadios(
                        'brief-has-dialogue',
                        [
                            { label: 'Yes', value: true },
                            { label: 'No', value: false }
                        ],
                        hasDialogue,
                        this.onHasDialogue
                    )}
                    {hasDialogue === true && (
                        <div className="brief-indent">
                            <fieldset className="brief-q">
                                <legend className="brief-label">
                                    How do characters address each other?
                                </legend>
                                {this.renderRadios(
                                    'brief-character-address',
                                    addressOptions,
                                    characterAddress,
                                    value =>
                                        this.onCheck('characterAddress', value)
                                )}
                            </fieldset>
                        </div>
                    )}
                </fieldset>
                <label className="brief-q">
                    <span className="brief-label">
                        Dialogue comments{' '}
                        <span className="brief-opt">Optional</span>
                    </span>
                    <TextInput
                        rows={3}
                        value={dialoguesInOrderNote}
                        maxLength={BRIEF_NOTE_MAX}
                        onChange={text =>
                            this.onInput('dialoguesInOrderNote', text)
                        }
                    />
                    <span className="brief-help">
                        Speaker names, gender and age group of the characters.
                        This changes the translation style in some languages.
                    </span>
                </label>
            </React.Fragment>
        );
    };

    renderTextDetails = () => {
        const { isSpecialCharacter, isAdaptation, adaptationNote } = this.state;
        return (
            <React.Fragment>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Can the game show special punctuation?
                    </legend>
                    <p className="brief-help">
                        Long dashes (—), curly quotes (“ ”) and non-breaking
                        spaces.
                    </p>
                    {this.renderRadios(
                        'brief-special-characters',
                        [
                            { label: 'Yes', value: true },
                            { label: 'No', value: false },
                            {
                                label: 'I don’t know, please check',
                                value: 'na'
                            }
                        ],
                        isSpecialCharacter,
                        value => this.onCheck('isSpecialCharacter', value)
                    )}
                </fieldset>
                <fieldset className="brief-q">
                    <legend className="brief-label">
                        Should we adapt currency, units and dates?
                    </legend>
                    <p className="brief-help">
                        Also measurements and references to local culture.
                    </p>
                    {this.renderRadios(
                        'brief-adaptation',
                        [
                            { label: 'No, keep them as written', value: false },
                            { label: 'Yes', value: true }
                        ],
                        isAdaptation,
                        value => this.onCheck('isAdaptation', value)
                    )}
                    {isAdaptation === true && (
                        <div className="brief-indent">
                            <label className="brief-q">
                                <span className="brief-label">
                                    What should we adapt?{' '}
                                    <span className="brief-opt">Optional</span>
                                </span>
                                <TextInput
                                    rows={3}
                                    value={adaptationNote}
                                    maxLength={BRIEF_NOTE_MAX}
                                    onChange={text =>
                                        this.onInput('adaptationNote', text)
                                    }
                                />
                            </label>
                        </div>
                    )}
                </fieldset>
            </React.Fragment>
        );
    };

    // ---------- screens ----------

    renderSummary = () => {
        const { sourceWordCount, quotation } = this.props;
        const currency =
            CURRENCIES[get(quotation, 'currency')] || DEFAULT_CURRENCY;
        const icon = get(currency, 'ICON');
        const sumCost = get(quotation, 'sumCost', 0);
        const minimumFee = get(quotation, 'minimumFee', MIN_PAYMENT_AMOUNT);
        return (
            <aside
                className="brief-summary"
                aria-labelledby="brief-summary-title"
            >
                <h2 id="brief-summary-title" className="brief-sum-h">
                    Order summary
                </h2>
                <dl>
                    <div className="brief-sum-row">
                        <dt>Source word count</dt>
                        <dd>{sourceWordCount}</dd>
                    </div>
                </dl>
                <p className="brief-sum-label">Price so far</p>
                <p className="brief-sum-price">
                    {icon} {sumCost}
                </p>
                <p className="brief-help">VAT not included.</p>
                {sumCost < minimumFee && (
                    <p className="brief-help brief-sum-min">
                        {`You pay the ${icon} ${minimumFee} minimum order.`}
                    </p>
                )}
            </aside>
        );
    };

    // Phones only (CSS): the order summary box is hidden there, so the
    // price sits on one line above the main button, like on step 3.
    renderPayLine = () => {
        const { quotation } = this.props;
        const currency =
            CURRENCIES[get(quotation, 'currency')] || DEFAULT_CURRENCY;
        const icon = get(currency, 'ICON');
        const sumCost = get(quotation, 'sumCost', 0);
        const minimumFee = get(quotation, 'minimumFee', MIN_PAYMENT_AMOUNT);
        return (
            <div className="brief-pay-line">
                <span className="brief-pay-label">
                    Price so far, VAT not included
                </span>
                <span className="brief-pay-price">
                    {icon} {sumCost}
                </span>
                {sumCost < minimumFee && (
                    <span className="brief-pay-min">
                        {`Minimum order, you pay ${icon} ${minimumFee}`}
                    </span>
                )}
            </div>
        );
    };

    renderBeforePayment = gameOptions => {
        const missing = this.missingBeforePayment();
        const { note } = this.state;
        const hasExtras =
            !!note || BRIEF_REF_TYPES.some(type => this.hasAssets(type.value));
        const showExtras =
            this.ui.showExtras === undefined ? hasExtras : this.ui.showExtras;
        const hasFiles = BRIEF_REF_TYPES.some(type =>
            this.hasAssets(type.value)
        );
        // Closing only hides the panel. Files and the note stay saved, and
        // the row says so.
        let extrasSub = 'Glossary, style guide, screenshots or a note';
        if (hasFiles && note) extrasSub = 'Files and a note added';
        else if (hasFiles) extrasSub = 'Files added';
        else if (note) extrasSub = 'Note added';

        return (
            <div className="brief-layout">
                <div className="brief-main">
                    <h2 className="brief-title" tabIndex={-1}>
                        Tell us about your game
                    </h2>
                    <p className="brief-why">
                        Just the basics so our linguists can get started. It
                        takes about a minute.
                    </p>
                    {this.ui.showErrors &&
                        missing.length > 0 &&
                        this.renderNotice(
                            'Almost there. Please add:',
                            missing.map(item => item[1])
                        )}
                    <div className="brief-body">
                        {this.renderGameQuestions(gameOptions)}
                        {this.renderTextRules()}
                        <div
                            className={`brief-extras-card${
                                showExtras ? ' is-open' : ''
                            }`}
                        >
                            <button
                                type="button"
                                className="brief-extras-toggle"
                                aria-expanded={showExtras}
                                aria-controls="brief-extras-panel"
                                onClick={() =>
                                    this.setUi({ showExtras: !showExtras })
                                }
                            >
                                <span
                                    className="brief-extras-icon"
                                    aria-hidden="true"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 01-7.8-7.8l8.6-8.6a3.7 3.7 0 015.2 5.2l-8.6 8.6a1.8 1.8 0 01-2.6-2.6l8-8" />
                                    </svg>
                                </span>
                                <span className="brief-extras-text">
                                    <span className="brief-extras-name">
                                        Reference files or a note{' '}
                                        <span className="brief-opt">
                                            Optional
                                        </span>
                                    </span>
                                    <span className="brief-extras-sub">
                                        {extrasSub}
                                    </span>
                                </span>
                                <span className="brief-extras-action">
                                    {showExtras && 'Close'}
                                    {!showExtras &&
                                        (hasExtras ? 'Edit' : 'Add')}
                                </span>
                            </button>
                            {showExtras ? (
                                <div
                                    id="brief-extras-panel"
                                    className="brief-extras"
                                >
                                    {this.renderExtras()}
                                </div>
                            ) : (
                                <div id="brief-extras-panel" hidden />
                            )}
                        </div>
                    </div>
                    <div className="brief-actions brief-actions-pay">
                        {this.renderPayLine()}
                        <button
                            type="button"
                            className="brief-btn"
                            onClick={this.onBriefFinish}
                        >
                            Continue to payment
                        </button>
                    </div>
                </div>
                {this.renderSummary()}
            </div>
        );
    };

    renderAfterPayment = () => {
        const { part, saving, saveError } = this.ui;
        const current = AFTER_PAYMENT_PARTS[part];
        let body = null;
        if (part === 1) body = this.renderGenreAndLinks();
        if (part === 2) body = this.renderAudience();
        if (part === 3) body = this.renderTone();
        if (part === 4) body = this.renderTextDetails();
        const isLast = part === AFTER_PAYMENT_PART_COUNT;
        const total = COUNTED_ANSWERS.length;
        const left = this.answersLeft();
        const percent = Math.round(((total - left) / total) * 100);
        let leftLabel = `${left} answers left`;
        if (left === 1) leftLabel = '1 answer left';
        if (left === 0) leftLabel = 'All answered';
        let saveLabel = isLast ? 'Save and finish' : 'Save and continue';
        if (saving) saveLabel = 'Saving…';

        return (
            <div className="brief-after-card">
                <div className="brief-after-head">
                    <div>
                        <p className="brief-after-kicker">
                            Help our linguists <br className="brief-br-phone" />
                            get it right{' '}
                            <span className="brief-opt brief-opt-phone">
                                Optional
                            </span>
                        </p>
                        <p className="brief-help">
                            Optional. A few quick answers make the translation
                            fit your game better.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="brief-text-link brief-after-skip"
                        onClick={() => this.finishAfterPayment('skipped')}
                    >
                        Skip for now
                    </button>
                </div>
                <div className="brief-progress brief-left">
                    <div className="brief-progress-row">
                        <span
                            className="brief-left-count"
                            role="status"
                            aria-live="polite"
                        >
                            {leftLabel}
                        </span>
                        <span className="brief-progress-name">
                            {`${total - left} of ${total} done`}
                        </span>
                    </div>
                    <div className="brief-bar" aria-hidden="true">
                        <div
                            className="brief-bar-fill"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                </div>
                <h2 className="brief-title" tabIndex={-1}>
                    {current.title}
                </h2>
                <p className="brief-why">{current.why}</p>
                {saveError &&
                    this.renderNotice(
                        'We couldn’t save these answers. Check your connection and try again, or skip for now.'
                    )}
                <div className="brief-body">{body}</div>
                <div className="brief-actions">
                    <button
                        type="button"
                        className="brief-btn"
                        disabled={saving}
                        onClick={this.onSaveAfterPaymentPart}
                    >
                        {saveLabel}
                    </button>
                    {part > 1 && (
                        <button
                            type="button"
                            className="brief-link"
                            disabled={saving}
                            onClick={() =>
                                this.setUi(
                                    { part: part - 1, saveError: false },
                                    'top'
                                )
                            }
                        >
                            Back
                        </button>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { orderList } = this.props;
        const isAfter = this.isAfterPayment();

        const gameOptions = orderList
            ? orderList.map(game => ({
                  label: game.project,
                  value: game.uid
              }))
            : [];
        gameOptions.sort((a, b) => {
            const value1 = a.label.toLowerCase();
            const value2 = b.label.toLowerCase();
            if (value1 < value2) {
                return -1;
            }
            if (value1 > value2) {
                return 1;
            }
            return 0;
        });

        this.briefErrors = {};
        if (!isAfter && this.ui.showErrors) {
            this.missingBeforePayment().forEach(item => {
                this.briefErrors[item[0]] = true;
            });
        }

        return (
            <div
                className={`step-page provide-order-info brief${
                    isAfter ? ' brief-after' : ''
                }`}
                ref={element => {
                    this.briefRoot = element;
                }}
            >
                {isAfter
                    ? this.renderAfterPayment()
                    : this.renderBeforePayment(gameOptions)}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const sourceWordCount = state.getIn([
        'order',
        'fileUploading',
        'data',
        'wordCount',
        'sourceWordCount'
    ]);

    return {
        sourceWordCount,
        assetFiles: state.getIn(['order', 'fileUploading', 'assets']),
        orderList: state.getIn(['customer', 'orders', 'data'])
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return bindActionCreators(
        {
            fillOrderInformation,
            uploadAssetFile,
            downloadAssetFile,
            removeAssetFile,
            fetchOrderList,
            saveInfoAfterPayment
        },
        dispatch
    );
};

export default DragDropContext(HTML5Backend)(
    connect(mapStateToProps, mapDispatchToProps)(toJS(OrderInformation))
);
